import {
  attachmentApi,
  FetchAttachmentsAndFoldersResponse,
  handleError,
} from '@ardoq/api';
import { ArdoqError } from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import {
  collectRoutines,
  dispatchAction,
  extractPayload,
  ofType,
  routine,
} from '@ardoq/rxbeach';
import {
  setWorkspaceDocumentArchiveAttachments,
  setWorkspaceDocumentArchiveFolders,
} from 'streams/documentArchive/actions';
import { setIsLoading } from 'components/GlobalPaneLoader/globalPaneLoader$';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { notifyWorkspaceChanged } from 'streams/context/ContextActions';
import { dishonestCurrentUser$ as currentUser$ } from 'streams/currentUser/currentUser$';
import { of } from 'rxjs';
import { isPresentationMode } from 'env';
import { permissionsOperations } from '@ardoq/access-control';
import { OrgAccessLevel } from '@ardoq/api-types';

const handleArdoqError = (error: ArdoqError) => {
  logError(error, 'Document Archive routine error');
  dispatchAction(setIsLoading(false));
};

/**
 * Needed to fetch attachments and folders when the workspace changes in workspace and presentation mode.
 * The fetched items are used to populate the attachment and folder streams.
 */
const handleFetchOnWorkspaceChange = routine(
  ofType(notifyWorkspaceChanged),
  extractPayload(),
  map(({ workspaceId }) => workspaceId),
  filter(Boolean),
  withLatestFrom(currentUser$),
  switchMap(([workspaceId, currentUser]) => {
    const emptyResponse = of({
      workspaceAttachments: [],
      workspaceFolders: [],
      orgFolders: [],
    } as FetchAttachmentsAndFoldersResponse);
    const organization = currentUser?.organization;
    if (!organization) return emptyResponse;
    // Skip fetching in presentation mode because presentations attachments are gotten from the aggregated presentation endpoint already
    // Also skip for contributors as the endpoint never applies to them
    if (
      isPresentationMode() ||
      permissionsOperations.getUserOrgAccessLevel(currentUser) ===
        OrgAccessLevel.CONTRIBUTOR
    ) {
      return emptyResponse;
    }
    return attachmentApi.fetchAttachmentsAndFolders(workspaceId);
  }),
  handleError(handleArdoqError),
  tap(({ workspaceAttachments, workspaceFolders }) => {
    dispatchAction(
      setWorkspaceDocumentArchiveAttachments(workspaceAttachments)
    );
    dispatchAction(setWorkspaceDocumentArchiveFolders(workspaceFolders));
  })
);

export const documentBrowserRoutines = collectRoutines(
  handleFetchOnWorkspaceChange
);
